import { captureException } from "@sentry/minimal";
import axios from "axios";
import Cookies from "js-cookie";
import mixpanel from "mixpanel-browser";
import { postDataNoTokenV2 } from "../api/utils";
import { __prod__ } from "../constants";
import { PATH_TO_TRACKING } from "./pathTracking";

export enum MixpanelEvents {
  login = "Login",
  logout = "Logout",
  menu_click = "Menu Click",
  cxo_access = "CXO Access",
  filter_reset = "Filter Reset",
  filter_change = "Filter Change",
  pdf_download = "PDF Download",
  csv_download = "CSV Download",
  dashboard_access_request = "Dashboard Access Request",
  data_info_click = "Data Info Click",
  volume_coversion_toggle = "Volume Conversion Toggle",
  save_as_home_page = "Save As Home Page",
  page_view = "Page View",
  metric_toggle = "Metric Toggle",
  search_result_click = "Search Result Click",
  search_abandoned = "Search Abandoned",
}

// Cached once — these are static device/browser properties that don't change per session.
let _mixpanelDefaults: Record<string, any> | null = null;
function getMixpanelDefaults(): Record<string, any> {
  if (_mixpanelDefaults) return _mixpanelDefaults;
  try {
    // @ts-ignore
    const { mp_lib, $lib_version, $insert_id, $current_url, time, ...defaults } = mixpanel["_"].info.properties();
    _mixpanelDefaults = defaults;
    return defaults;
  } catch {
    return {};
  }
}

// Single in-flight promise so concurrent calls don't each fire a separate ipify request.
let _ipPromise: Promise<string> | null = null;
function getIp(): Promise<string> {
  if (_ipPromise) return _ipPromise;
  const cached = sessionStorage.getItem("ip");
  if (cached) return Promise.resolve(cached);
  _ipPromise = axios
    .get("https://api.ipify.org?format=json")
    .then((res) => {
      sessionStorage.setItem("ip", res.data.ip);
      return res.data.ip as string;
    })
    .catch(() => "");
  return _ipPromise;
}

export const eventTracking = (event: MixpanelEvents, eventProperties: Record<string, any> = {}) => {
  const { dashboard: _d, page: callerPage, ...rest } = eventProperties;
  const tracking = PATH_TO_TRACKING[window.location.pathname];

  if (!tracking) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[tracking] No entry for "${window.location.pathname}" in PATH_TO_TRACKING. Add it to get dashboard/page tracking.`);
    }
    return;
  }

  const payload: Record<string, any> = {
    ...tracking,
    ...(callerPage !== undefined ? { page: callerPage } : {}),
    ...rest,
  };

  if (__prod__ && Cookies.get("on_ga") === "true") {
    getIp().then((ip) => {
      try {
        const value =
          event === MixpanelEvents.filter_change && typeof payload.value === "string"
            ? payload.value.split(",")
            : payload.value;
        postDataNoTokenV2(
          `/usage-tracking?event=${event}`,
          { ...getMixpanelDefaults(), ...payload, ip, ...(value !== undefined ? { value } : {}) },
          Cookies.get("access_token"),
        );
      } catch (err) {
        captureException(err);
      }
    });
  }
};
